import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Container,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Pagination,
  InputAdornment,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add,
  Refresh,
  Receipt,
  Visibility,
  Search,
  FilterList,
  TrendingUp,
  HourglassEmpty,
  CheckCircle,
  Error as ErrorIcon,
  AccountBalanceWallet,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import transactionService from '../services/transactionService';
import { useAuth } from '../hooks/useAuth';
import { Transaction } from '../types';
import TransactionDetailDialog from '../components/TransactionDetailDialog';
import CreateTransactionDialog from '../components/CreateTransactionDialog';

interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color, loading }) => {
  const theme = useTheme();
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderLeft: `4px solid ${color}`,
        bgcolor: alpha(color, 0.04),
      }}
    >
      <CardContent sx={{ py: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <CircularProgress size={24} sx={{ my: 1 }} />
            ) : (
              <Typography variant="h5" fontWeight={700} sx={{ color }}>
                {value}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color, opacity: 0.85 }}>{icon}</Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const TYPE_LABELS: Record<string, string> = {
  topup: 'Top Up',
  payment: 'Pembayaran',
  deduction: 'Pemotongan',
  refund: 'Refund',
};

const STATUS_LABELS: Record<string, string> = {
  completed: 'Selesai',
  pending: 'Pending',
  failed: 'Gagal',
  cancelled: 'Dibatalkan',
};

const Transactions: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const spopFilter = user?.role === 'operator' && user?.spop_id ? user.spop_id : undefined;

  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  const monthRange = useMemo(() => {
    const now = new Date();
    return {
      start_date: format(startOfMonth(now), 'yyyy-MM-dd'),
      end_date: format(endOfMonth(now), 'yyyy-MM-dd'),
      label: format(now, 'MMMM yyyy', { locale: localeId }),
    };
  }, []);

  const listFilters = useMemo(() => {
    const f: Record<string, string> = {};
    if (spopFilter) f.spop_id = spopFilter;
    if (search) f.search = search;
    if (typeFilter) f.type = typeFilter;
    if (statusFilter) f.status = statusFilter;
    if (startDate) f.start_date = startDate;
    if (endDate) f.end_date = endDate;
    return f;
  }, [spopFilter, search, typeFilter, statusFilter, startDate, endDate]);

  const statsFilters = useMemo(() => {
    const f: Record<string, string> = {
      start_date: monthRange.start_date,
      end_date: monthRange.end_date,
    };
    if (spopFilter) f.spop_id = spopFilter;
    return f;
  }, [monthRange, spopFilter]);

  const {
    data: transactionsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['transactions', page, limit, listFilters],
    queryFn: () => transactionService.getTransactions(page, limit, undefined, listFilters),
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['transactionStats', statsFilters],
    queryFn: () => transactionService.getTransactionStats(statsFilters),
  });

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return '-';
      return format(new Date(dateString), 'dd/MM/yy HH:mm', { locale: localeId });
    } catch {
      return '-';
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string): 'success' | 'primary' | 'error' | 'info' | 'default' => {
    switch (type) {
      case 'topup':
        return 'success';
      case 'payment':
        return 'primary';
      case 'deduction':
        return 'error';
      case 'refund':
        return 'info';
      default:
        return 'default';
    }
  };

  const summary = useMemo(() => {
    const statusStats = statsData?.data?.statusStats || [];
    const typeStats = statsData?.data?.typeStats || [];

    const findStatus = (s: string) => statusStats.find((x: any) => x.status === s);
    const paymentStats = typeStats.find((x: any) => x.type === 'payment');

    return {
      revenue: parseFloat(String(paymentStats?.total_amount ?? 0)),
      paymentCount: parseInt(String(paymentStats?.count ?? 0), 10),
      pending: parseInt(String(findStatus('pending')?.count ?? 0), 10),
      completed: parseInt(String(findStatus('completed')?.count ?? 0), 10),
      failed: parseInt(String(findStatus('failed')?.count ?? 0), 10),
    };
  }, [statsData]);

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setSearch('');
    setTypeFilter('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const handleView = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setDetailOpen(true);
  };

  const handleRefresh = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['transactionStats'] });
  };

  const transactions = transactionsData?.data?.transactions || [];
  const pagination = transactionsData?.data?.pagination;

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error">
          Gagal memuat transaksi: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Transaksi
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Riwayat pembayaran, top up, dan mutasi saldo pelanggan
            {user?.role === 'operator' && user?.spop
              ? ` • SPOP ${user.spop.name}`
              : ' • Semua SPOP'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Tambah Transaksi
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pembayaran Selesai"
            value={formatCurrency(summary.revenue)}
            subtitle={`${summary.paymentCount} transaksi • ${monthRange.label}`}
            icon={<TrendingUp fontSize="large" />}
            color={theme.palette.success.main}
            loading={statsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Transaksi Selesai"
            value={String(summary.completed)}
            subtitle="Bulan berjalan"
            icon={<CheckCircle fontSize="large" />}
            color={theme.palette.primary.main}
            loading={statsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Menunggu"
            value={String(summary.pending)}
            subtitle="Perlu diproses"
            icon={<HourglassEmpty fontSize="large" />}
            color={theme.palette.warning.main}
            loading={statsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Gagal / Dibatalkan"
            value={String(summary.failed)}
            subtitle="Bulan berjalan"
            icon={<ErrorIcon fontSize="large" />}
            color={theme.palette.error.main}
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" gap={1} mb={2}>
            <FilterList fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              Filter
            </Typography>
          </Stack>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Cari kode / deskripsi"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Jenis</InputLabel>
                <Select
                  value={typeFilter}
                  label="Jenis"
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">Semua</MenuItem>
                  <MenuItem value="payment">Pembayaran</MenuItem>
                  <MenuItem value="topup">Top Up</MenuItem>
                  <MenuItem value="deduction">Pemotongan</MenuItem>
                  <MenuItem value="refund">Refund</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">Semua</MenuItem>
                  <MenuItem value="completed">Selesai</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Gagal</MenuItem>
                  <MenuItem value="cancelled">Dibatalkan</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Dari tanggal"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Sampai tanggal"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 1 }}>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" size="small" onClick={handleSearch} fullWidth>
                  Cari
                </Button>
                <Button variant="text" size="small" onClick={handleResetFilters}>
                  Reset
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Kode</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Pelanggan</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Jenis</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Jumlah
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Metode</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Tanggal</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">
                        Aksi
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.length > 0 ? (
                      transactions.map((transaction: Transaction) => (
                        <TableRow key={transaction.id} hover>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Receipt fontSize="small" color="action" />
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 140 }}>
                                {transaction.transaction_code}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                              {transaction.customer?.name || '-'}
                            </Typography>
                            {transaction.customer?.customer_code && (
                              <Typography variant="caption" color="text.secondary">
                                {transaction.customer.customer_code}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={TYPE_LABELS[transaction.type] || transaction.type}
                              color={getTypeColor(transaction.type)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {formatCurrency(transaction.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={STATUS_LABELS[transaction.status] || transaction.status}
                              color={getStatusColor(transaction.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 100 }}>
                              {transaction.payment_method || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDate(transaction.createdAt)}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Detail">
                              <IconButton size="small" onClick={() => handleView(transaction)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                          <AccountBalanceWallet sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                          <Typography color="text.secondary">Tidak ada transaksi ditemukan</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {pagination && pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {(page - 1) * limit + 1}–{Math.min(page * limit, pagination.total)} dari {pagination.total}{' '}
                    transaksi
                  </Typography>
                  <Pagination
                    count={pagination.totalPages}
                    page={page}
                    onChange={(_, p) => setPage(p)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <TransactionDetailDialog
        open={detailOpen}
        transaction={selectedTransaction}
        onClose={() => setDetailOpen(false)}
        onUpdated={handleRefresh}
      />

      <CreateTransactionDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleRefresh}
        spopFilter={spopFilter}
      />
    </Container>
  );
};

export default Transactions;
